import { splitDecimal } from '@/src/lib/money';

/**
 * Adding and multiplying money without a `number` ever touching it.
 *
 * <p>`src/lib/money.ts` renders a decimal string; it deliberately never does
 * arithmetic, because the storefront had none to do — the backend sent every
 * total already summed. An ordering surface does: the price of a dish plus the
 * options chosen in a modifier dialog has to be shown BEFORE the line exists
 * server-side, and `2.50 + 0.45` through IEEE-754 is `2.9499999999999997`.
 *
 * <p>So the digits are moved as integers. Every operand is split by
 * {@link splitDecimal} — the app's own parser, reused rather than re-written —
 * rescaled to a common number of fraction digits, and summed as `bigint` minor
 * units. Nothing is rounded: the result carries the widest scale of its inputs,
 * so a scale-4 unit price times a scale-3 quantity comes back at scale 7 and
 * the caller decides how many digits to SHOW. Rounding a total for display is
 * `formatMoney`'s job; rounding one for money is the backend's.
 *
 * <p>These are for display only. The authoritative subtotal, tax and total are
 * always the ones `CartDto` returns, computed by the pricing engine against the
 * channel's tax rules — never re-derived here.
 */

interface Scaled {
  units: bigint;
  scale: number;
}

function scaled(amount: string | null | undefined): Scaled | null {
  const split = splitDecimal(amount);
  if (!split) return null;
  const digits = `${split.integer}${split.fraction}`;
  const units = BigInt(digits);
  return { units: split.negative ? -units : units, scale: split.fraction.length };
}

function rescale(value: Scaled, scale: number): bigint {
  if (scale === value.scale) return value.units;
  return value.units * 10n ** BigInt(scale - value.scale);
}

/** A scaled integer back to a plain decimal string, sign and all. */
function render(units: bigint, scale: number): string {
  const negative = units < 0n;
  const digits = (negative ? -units : units).toString().padStart(scale + 1, '0');
  const body = scale === 0 ? digits : `${digits.slice(0, -scale)}.${digits.slice(-scale)}`;
  return negative ? `-${body}` : body;
}

/**
 * The sum of decimal strings, at the widest scale any of them used.
 *
 * <p>An unparseable or absent operand contributes nothing rather than poisoning
 * the sum with `NaN` — a modifier whose price the backend left null adds
 * nothing, which is exactly what a null price means.
 */
export function addDecimals(values: ReadonlyArray<string | null | undefined>): string {
  const parsed = values.map(scaled).filter((v): v is Scaled => v !== null);
  if (parsed.length === 0) return '0';
  const scale = parsed.reduce((max, v) => (v.scale > max ? v.scale : max), 0);
  const total = parsed.reduce((sum, v) => sum + rescale(v, scale), 0n);
  return render(total, scale);
}

/** The product of two decimal strings, at the sum of their scales. `null` when
 *  either side is not a decimal — a caller must not silently read that as 0. */
export function multiplyDecimals(a: string | null | undefined, b: string | null | undefined): string | null {
  const left = scaled(a);
  const right = scaled(b);
  if (!left || !right) return null;
  return render(left.units * right.units, left.scale + right.scale);
}

/** `-1`, `0` or `1`, comparing two decimal strings exactly. An unparseable
 *  operand sorts as absent (`0` against another absent, below anything real). */
export function compareDecimals(a: string | null | undefined, b: string | null | undefined): number {
  const left = scaled(a);
  const right = scaled(b);
  if (!left && !right) return 0;
  if (!left) return -1;
  if (!right) return 1;
  const scale = Math.max(left.scale, right.scale);
  const l = rescale(left, scale);
  const r = rescale(right, scale);
  return l === r ? 0 : l < r ? -1 : 1;
}

/** Whether a decimal string is exactly zero (or absent). */
export function isZeroDecimal(value: string | null | undefined): boolean {
  const parsed = scaled(value);
  return parsed === null || parsed.units === 0n;
}
